import React, {
  Fragment,
  FunctionComponent,
  useState,
  createElement,
  useEffect,
} from 'react';
import { first, get, mapValues } from 'lodash';
import {
  Button as RaButton,
  useUpdateMany,
  useRefresh,
  useNotify,
  useUnselectAll,
  useTranslate,
  useUpdate,
  useListContext,
} from 'react-admin';
import ConfirmationModal from '../../../modals/ConfirmationModal';
import { getIconComponentFromName } from '../../../../helpers/icons';
import checkConditions from '../../../../helpers/conditions';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  DialogContentText as MuiDialogContentText,
} from '@material-ui/core';
import getUrlPaths from '../../../../helpers/getUrlPath';
import useRefreshWhenDone from '../../../../hooks/useRefreshWhenDone';
import { getErrorMessage } from '../../../../helpers/error';
import { UpdateButtonProps } from './types';

const useStyles = makeStyles({
  button: {
    marginLeft: '10px',
  },
  errorContent: {
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
  },
});

const UpdateButton: FunctionComponent<UpdateButtonProps> = (props) => {
  const {
    selectedIds = [],
    resource,
    values,
    label,
    extraUrl,
    withConfirmation,
    confirmation,
    conditionalConfirmations,
    icon,
    data,
    afterUpdate = null,
    disabled: disabledRules,
    blockIf,
    successMessage,
    refreshWhenDone,
    checkUnsavedChanges,
  } = props;
  const { data: listData } = useListContext();
  const classes = useStyles();

  if (typeof resource === 'undefined') {
    throw new Error(`Missing prop resource for UpdateButton component`);
  }

  const t = useTranslate();
  const [showDialog, setShowDialog] = useState(false);
  // The conditional confirmation the record calls for, if any
  const [activeConfirmation, setActiveConfirmation] = useState<
    NonNullable<typeof conditionalConfirmations>[number] | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const startRefreshWhenDone = useRefreshWhenDone(resource, refreshWhenDone);

  const [update, { data: result, loading, error, loaded }] = (function () {
    const ids = getUrlPaths(selectedIds, extraUrl);
    if (Array.isArray(selectedIds)) {
      return useUpdateMany(resource, ids, values, {
        onSuccess: () => {
          refresh();
          if (successMessage) notify(successMessage, 'success');
          else notify('notify.success.update');
          unselectAll(resource);
          startRefreshWhenDone(selectedIds as string[]);
        },
        onFailure: (errors: Record<string, any>) => {
          if (Array.isArray(errors) && errors.length) {
            errors.forEach((error) => {
              const errorBody = get(error, 'error.body');
              if (get(errorBody, 'error') === 'RuntimeError') {
                notify(`notify.error.${get(errorBody, 'message')}`, 'error');
              } else {
                const errorMessage = getErrorMessage(error);
                if (typeof errorMessage !== 'undefined') {
                  notify(`notify.error.${errorMessage}`, 'error', {
                    id: error.id,
                  });
                }
              }
            });
          } else {
            notify('notify.error.update', 'error');
          }
        },
      });
    } else {
      const id = first(ids);

      // Only pass callbacks when configured, to keep react-admin's default
      // behaviour for the other single update buttons.
      // Signature is (resource, id, data, previousData, options): the options
      // must be the 5th argument, otherwise onSuccess is silently ignored.
      return useUpdate(
        resource,
        id,
        values,
        undefined,
        successMessage || refreshWhenDone
          ? {
              onSuccess: () => {
                if (successMessage) notify(successMessage, 'success');
                startRefreshWhenDone([selectedIds as string]);
              },
            }
          : undefined
      );
    }
  })();

  const refresh = useRefresh();
  const notify = useNotify();
  const unselectAll = useUnselectAll();

  function handleClick() {
    update();
    setShowDialog(false);
    setActiveConfirmation(null);
  }

  function handleOpenModal() {
    setShowDialog(true);
  }

  function handleCloseModal() {
    setShowDialog(false);
    setActiveConfirmation(null);
  }

  const disabled =
    disabledRules && data
      ? checkConditions(
          {
            rules: disabledRules,
            satisfy: 'ALL',
          },
          data
        )
      : false;

  if (error && !Array.isArray(error)) {
    const errorMessage = getErrorMessage(error);
    if (typeof errorMessage !== 'undefined') {
      notify(`notify.error.${errorMessage}`, 'error');
    }
  }

  useEffect(() => {
    if (loaded && afterUpdate && result && !Array.isArray(selectedIds)) {
      afterUpdate(result);
    }
  }, [loaded, result]);

  const IconComponent = getIconComponentFromName(icon);

  const handleButtonClick = () => {
    if (blockIf && Array.isArray(selectedIds) && listData) {
      const offendingId = (selectedIds as string[]).find((id) =>
        checkConditions(
          { rules: blockIf.conditions, satisfy: 'ALL' },
          listData[id] || {}
        )
      );
      if (offendingId) {
        const record = listData[offendingId] || {};
        const ref = blockIf.referenceProperty
          ? record[blockIf.referenceProperty] ?? offendingId
          : offendingId;
        notify(
          t(blockIf.errorKey, { [blockIf.referenceProperty || 'id']: ref }),
          'error'
        );

        return;
      }
    }
    // The action works on the saved record: unsaved changes (e.g. another
    // contract template chosen) must be saved first
    if (
      props.page === 'edit' &&
      (extraUrl === '/validate' || checkUnsavedChanges)
    ) {
      const getFormState = (window as any).__form_state__;
      const isFormDirty = getFormState
        ? getFormState().dirty
        : (window as any).__form_dirty__;
      if (isFormDirty) {
        setErrorMessage(t('notify.error.unsaved_changes'));

        return;
      }
    }
    const matchingConfirmation =
      data &&
      conditionalConfirmations?.find(({ rules }) =>
        checkConditions({ rules, satisfy: 'ALL' }, data)
      );
    if (matchingConfirmation) {
      setActiveConfirmation(matchingConfirmation);
      handleOpenModal();
    } else if (withConfirmation) {
      handleOpenModal();
    } else {
      update();
    }
  };

  const confirmationTitle = activeConfirmation
    ? activeConfirmation.title
    : confirmation?.title;
  const confirmationContent = activeConfirmation
    ? t(
        activeConfirmation.content,
        mapValues(activeConfirmation.values || {}, (property) =>
          get(data, property)
        )
      )
    : confirmation?.content && t(`${confirmation.content}`, data);

  return (
    <Fragment>
      <RaButton
        label={label}
        disabled={loading || disabled}
        variant={withConfirmation ? 'contained' : 'outlined'}
        onClick={handleButtonClick}
        className={classes.button}
        {...(IconComponent && { startIcon: createElement(IconComponent) })}
      />
      <ConfirmationModal
        open={showDialog}
        title={confirmationTitle && t(confirmationTitle)}
        handleClick={handleClick}
        handleClose={handleCloseModal}
      >
        {confirmationContent}
      </ConfirmationModal>
      <MuiDialog open={!!errorMessage} onClose={() => setErrorMessage(null)}>
        <MuiDialogTitle>{t('notify.error.title')}</MuiDialogTitle>
        <MuiDialogContent>
          <MuiDialogContentText className={classes.errorContent}>
            {errorMessage}
          </MuiDialogContentText>
        </MuiDialogContent>
        <MuiDialogActions>
          <RaButton
            onClick={() => {
              setErrorMessage(null);
              (window as any).__reload_after_save__ = true;
              setTimeout(() => {
                const submitBtn = document.querySelector(
                  'form button[type="submit"]'
                ) as HTMLButtonElement | null;
                if (submitBtn) submitBtn.click();
              }, 0);
            }}
            label={t('ra.action.save')}
          />
          <RaButton
            onClick={() => setErrorMessage(null)}
            label={t('modal.confirm.cancel')}
          />
        </MuiDialogActions>
      </MuiDialog>
    </Fragment>
  );
};

export default UpdateButton;
